// MobileNav — Minimal 48px gesture bar replacing the legacy 5-tab nav.
// Layout: [active module indicator | drag-to-open pill | alerts bell + menu]
// Tap center pill or drag up → opens the nav drawer.

import { useRef } from 'react';
import { Bell, AlignJustify, ChevronUp } from 'lucide-react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';

const MODULE_LABELS: Record<string, { label: string; emoji: string }> = {
  diamondedge:  { label: 'DiamondEdge',  emoji: '⚾' },
  courtedge:    { label: 'CourtEdge',    emoji: '🏀' },
  griironedge:  { label: 'GridironEdge', emoji: '🏈' },
  iceedge:      { label: 'IceEdge',      emoji: '🏒' },
  pitchedge:    { label: 'PitchEdge',    emoji: '⚽' },
  octagonedge:  { label: 'OctagonEdge',  emoji: '🥊' },
  pulse:        { label: 'Pulse',        emoji: '⚡' },
};

export function MobileNav() {
  const { activeModuleId, unreadCount, setMobileDrawerOpen } = useAppStore();
  const dragY = useMotionValue(0);
  const pillOpacity = useTransform(dragY, [-60, 0], [0, 1]);
  const chevronOpacity = useTransform(dragY, [-60, -8], [1, 0]);
  const constraintsRef = useRef<HTMLDivElement>(null);

  const module = MODULE_LABELS[activeModuleId] ?? { label: 'Pulse', emoji: '⚡' };

  const openDrawer = () => setMobileDrawerOpen(true, 'nav');

  const handleDragEnd = (_: unknown, info: { offset: { y: number }; velocity: { y: number } }) => {
    const didSwipeUp = info.offset.y < -32 || info.velocity.y < -300;
    animate(dragY, 0, { type: 'spring', stiffness: 500, damping: 40 });
    if (didSwipeUp) openDrawer();
  };

  return (
    <nav
      ref={constraintsRef}
      className="flex md:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface-1/95 backdrop-blur-sm border-t border-surface-4"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center w-full h-12 px-4">

        {/* Left — active module label */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-base leading-none">{module.emoji}</span>
          <span className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase truncate">
            {module.label}
          </span>
        </div>

        {/* Center — drag-to-open pill */}
        <motion.button
          drag="y"
          dragConstraints={constraintsRef}
          dragElastic={0.3}
          style={{ y: dragY }}
          onDragEnd={handleDragEnd}
          onClick={openDrawer}
          whileTap={{ scale: 0.95 }}
          className="flex flex-col items-center gap-1 px-6 cursor-pointer select-none"
          aria-label="Open navigation"
        >
          <motion.div style={{ opacity: chevronOpacity }} className="absolute -top-1">
            <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
          </motion.div>
          <motion.div
            style={{ opacity: pillOpacity }}
            className="w-10 h-1 rounded-full bg-surface-4"
          />
          <span className="text-[9px] font-medium text-gray-600 uppercase tracking-widest">
            Menu
          </span>
        </motion.button>

        {/* Right — alerts + menu */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <button
            onClick={() => setMobileDrawerOpen(true, 'nav')}
            className={clsx(
              'relative flex items-center justify-center w-8 h-8 rounded-lg',
              'text-gray-500 active:bg-surface-3 transition-colors',
            )}
            aria-label="Alerts"
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 px-0.5 bg-warn text-[9px] text-surface-0 rounded-full flex items-center justify-center font-bold leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={openDrawer}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 active:bg-surface-3 transition-colors"
            aria-label="Main menu"
          >
            <AlignJustify className="w-4 h-4" />
          </button>
        </div>
      </div>
    </nav>
  );
}
